import type { AgenticLogicRuleDto } from "@intelligems/ig-types";

import { useAgenticLogicRuleVersions } from "@src/api/agentic-logic/get-agentic-logic-rule-versions";
import { useAgenticLogicRuleDraft } from "@src/api/agentic-logic/get-agentic-logic-rule-draft";
import { usePublishedRuleSource } from "./usePublishedRuleSource";

import {
  hasUnpublishedRuleConfig,
  hasUnpublishedRuleSettings,
} from "./unpublishedSettings";

/** Compare the saved draft with the published version. Local edits are tracked by the editor. */
export const useUnpublishedRuleChanges = (
  rule: AgenticLogicRuleDto
): {
  hasUnpublishedChanges: boolean;
  isChecking: boolean;
  comparisonFailed: boolean;
} => {
  const versionsQuery = useAgenticLogicRuleVersions(rule.id);
  const activeVersion =
    versionsQuery.data?.versions.find(
      (version) => version.status === "active"
    ) ?? null;
  const isCode = rule.managedBy === "code";
  const draftQuery = useAgenticLogicRuleDraft(isCode ? rule.id : undefined);
  const publishedSource = usePublishedRuleSource(
    rule.id,
    isCode ? (activeVersion?.version ?? null) : null
  );

  return {
    isChecking:
      versionsQuery.isLoading ||
      (isCode && (draftQuery.isLoading || publishedSource.isLoading)),
    comparisonFailed:
      versionsQuery.isError ||
      (isCode && (draftQuery.isError || publishedSource.isError)),
    hasUnpublishedChanges:
      hasUnpublishedRuleSettings(
        rule.settings,
        activeVersion?.settings ?? null
      ) ||
      hasUnpublishedRuleConfig(
        rule.templateParams,
        activeVersion?.templateParams ?? null
      ) ||
      (isCode &&
        draftQuery.data?.draft?.source != null &&
        publishedSource.data !== undefined &&
        draftQuery.data.draft.source !== publishedSource.data),
  };
};
